// Standard Library
// 3rd Party
// Local
import { UriAwareMixin } from './mixins/uri-aware.js';

/**
 * Uri Router — maps a URL hash fragment to which light-DOM child is
 * projected into the shadow `content` slot.
 *
 * ## default
 * Starting route when the fragment has no value yet.
 *
 * ## default-mode (Issue #400)
 * - `eager` (default): persist `default` into the hash on connect if empty
 *   (legacy behavior; use for top-level pane routers).
 * - `lazy`: resolve default for display via `effectiveValue`, but only
 *   **write** it into the hash when this router is active in the nav stack:
 *   `hashMap[active-when-fragment] === active-when-value`.
 *   Nested workspace sub-routers use lazy so a new pane does not seed every
 *   view's subview defaults into the URL.
 *
 * ## active-when-fragment / active-when-value
 * Gate for lazy persist. If `active-when-fragment` is omitted, lazy is
 * treated as un-gated (persist allowed when empty) — prefer setting both
 * for nested routers.
 */
// eslint-disable-next-line new-cap
class UriRouterElement extends UriAwareMixin(HTMLElement) {
  /**
   * @returns {string[]}
   */
  static get observedAttributes() {
    return [
      'uri',
      'fragment',
      'data-fragment-name',
      'default',
      'default-mode',
      'active-when-fragment',
      'active-when-value',
    ];
  }

  constructor() {
    super();
    this.attachShadow({ mode: 'open' });
    const container = document.createElement('div');
    container.innerHTML = `
      <style>
        /* Subtle fade-in on every newly-projected route — fires when a
         * tab/view switch reslot()s a different child element. Opacity
         * only (no transform) so layout never shifts. Honored
         * automatically by prefers-reduced-motion via base.css. */
        @keyframes uri-router-route-in {
          from { opacity: 0; }
          to { opacity: 1; }
        }
        ::slotted(*) {
          animation: uri-router-route-in 0.18s ease-out;
        }
      </style>
      <slot id="content" name="content"></slot>
    `;
    this.shadowRoot.appendChild(container);
    this._onHashChange = this.onHashChange.bind(this);
    this._syncingUri = false;
  }

  /**
   * @param {string} name
   * @param {string|null} oldValue
   * @param {string|null} newValue
   */
  attributeChangedCallback(name, oldValue, newValue) {
    const hasValue = newValue !== null;

    if (name === 'uri' && hasValue) {
      // Intentional navigation sets uri and should update the hash.
      // Internal sync from hashchange must not re-write (esp. effective
      // default while still lazy-unpersisted is handled by ensureDefault).
      if (!this._syncingUri && this.hashMap[this.fragment] !== newValue) {
        this.hashValue = newValue;
      }
      this.slotPage();
    }
  }

  connectedCallback() {
    if (!this.hasAttribute('data-fragment-name')) {
      this.setAttribute('data-fragment-name', 'data-fragment');
    }

    window.addEventListener('hashchange', this._onHashChange, false);

    this._observer = new MutationObserver(() => this.slotPage());
    this._observer.observe(this, { childList: true });

    // Eager: write default on connect. Lazy: only if already active in stack.
    this.ensureDefaultPersisted();
    this.slotPage();
    // Notify peers (nav / sibling routers) without forcing another write path
    // that depends solely on hashValue being set.
    window.dispatchEvent(new Event('hashchange'));
  }

  disconnectedCallback() {
    window.removeEventListener('hashchange', this._onHashChange, false);
    if (this._observer) {
      this._observer.disconnect();
      this._observer = null;
    }
  }

  get _$Map() {
    return {
      $content: this.shadowRoot.querySelector('slot#content'),
    };
  }

  set dataFragmentName(newFragmentName) {
    if (newFragmentName !== null) {
      this.setAttribute('data-fragment-name', newFragmentName);
    }
  }

  get dataFragmentName() {
    return this.getAttribute('data-fragment-name');
  }

  /**
   * Default route name from the `default` attribute.
   * @returns {string|null}
   */
  get defaultView() {
    return this.getAttribute('default');
  }

  /**
   * `eager` | `lazy` — default eager for back-compat (Issue #400).
   * @returns {'eager'|'lazy'}
   */
  get defaultMode() {
    const mode = (this.getAttribute('default-mode') || 'eager').toLowerCase();
    return mode === 'lazy' ? 'lazy' : 'eager';
  }

  /**
   * Parent fragment key that must match for lazy persist (e.g. pane-6).
   * @returns {string|null}
   */
  get activeWhenFragment() {
    return this.getAttribute('active-when-fragment');
  }

  /**
   * Required value of the parent fragment (e.g. reservations).
   * @returns {string|null}
   */
  get activeWhenValue() {
    return this.getAttribute('active-when-value');
  }

  /**
   * Route used for slotting: explicit hash value, else default.
   * @returns {string|undefined}
   */
  get effectiveValue() {
    const fromHash = this.hashValue;
    if (fromHash !== undefined && fromHash !== null && fromHash !== '') {
      return fromHash;
    }
    const d = this.defaultView;
    return d || undefined;
  }

  /**
   * Whether this router may persist its default into the URL right now.
   * Eager always; lazy only when the parent view is the active one.
   * @returns {boolean}
   */
  isActiveInStack() {
    if (this.defaultMode === 'eager') return true;
    const parentFrag = this.activeWhenFragment;
    // No gate declared → allow persist (lazy without parent ≈ eager write)
    if (!parentFrag) return true;
    return this.hashMap[parentFrag] === this.activeWhenValue;
  }

  /**
   * Write `default` into the hash when empty and policy allows.
   *
   * Always uses replaceState (temporary noHistory) so seeding defaults never
   * creates a browser history entry. Otherwise, after a real navigation
   * (or Back), eager/lazy ensureDefault can immediately re-push and make
   * Back appear to do nothing — especially with multi-pane stacks (#510).
   */
  ensureDefaultPersisted() {
    if (!this.defaultView) return;
    if (!this.fragment) return;
    const current = this.hashMap[this.fragment];
    if (current !== undefined && current !== null && current !== '') return;
    if (!this.isActiveInStack()) return;
    const prev = this.noHistory;
    this.noHistory = true;
    try {
      this.hashValue = this.defaultView;
    } finally {
      this.noHistory = prev;
    }
  }

  /**
   * @returns {HTMLElement[]}
   */
  get routes() {
    const allRoutes = Array.from(this.children);
    return allRoutes.filter((child) => {
      if (child.getAttribute(this.dataFragmentName)) {
        return child;
      }
    });
  }

  /**
   * Project the child matching effectiveValue into the content slot.
   */
  slotPage() {
    const active = this.effectiveValue;
    this.routes.map((p) => {
      if (active !== undefined && p.getAttribute(this.dataFragmentName) === active) {
        p.setAttribute('slot', 'content');
      } else {
        p.removeAttribute('slot');
      }
    });
  }

  /**
   * Hash changed — maybe persist lazy default, then reslot.
   */
  onHashChange() {
    this.ensureDefaultPersisted();
    const value = this.effectiveValue;
    if (value !== undefined) {
      this._syncingUri = true;
      this.setAttribute('uri', value);
      this._syncingUri = false;
    }
    this.slotPage();
  }
}

export { UriRouterElement };
